import { useState } from "react";
import { Button } from "../button";
import { Icon } from "../icons/icon";
import { AddFieldButton } from "./AddFieldButton";
import { FieldDisplay } from "./FieldDisplay";
import { InlineInputControl } from "./InlineInputControl";

interface EditableListProps {
  values: string[];
  /** Called when user commits a change. Resolve = exit edit mode, reject = stay in edit mode. */
  onSave: (values: string[]) => Promise<void>;
  /** Called when user modifies input (for clearing errors, live validation, etc.) */
  onChange?: (values: string[]) => void;
  placeholder?: string;
  addLabel: string;
  canEdit?: boolean;
  /** Error message to display while editing. Controlled by parent. */
  error?: string;
  /** Loading state while saving. Controlled by parent. */
  saving?: boolean;
}

export function EditableList({
  values,
  onSave,
  onChange,
  placeholder,
  addLabel,
  canEdit,
  error,
  saving = false,
}: EditableListProps) {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [editValue, setEditValue] = useState("");

  const icon = <Icon i="external-link" className="size-4" />;
  const editIcon = <Icon i="link" className="size-4" />;

  const startEditing = (index: number, value: string) => {
    setEditingIndex(index);
    setIsAdding(false);
    setEditValue(value);
  };

  const startAdding = () => {
    setIsAdding(true);
    setEditingIndex(null);
    setEditValue("");
  };

  const cancelEditing = () => {
    setEditingIndex(null);
    setIsAdding(false);
    setEditValue("");
  };

  const handleChange = (newValue: string) => {
    setEditValue(newValue);

    // Compute what the list would look like
    let trimmed = newValue.trim();
    if (trimmed && !/^https?:\/\//i.test(trimmed)) {
      trimmed = `https://${trimmed}`;
    }

    if (isAdding) {
      onChange?.(trimmed ? [...values, trimmed] : values);
    } else if (editingIndex !== null) {
      if (!trimmed) {
        onChange?.(values.filter((_, i) => i !== editingIndex));
      } else {
        onChange?.(values.map((v, i) => (i === editingIndex ? trimmed : v)));
      }
    }
  };

  const save = async () => {
    let trimmed = editValue.trim();

    // Auto-prefix https://
    if (trimmed && !/^https?:\/\//i.test(trimmed)) {
      trimmed = `https://${trimmed}`;
    }

    try {
      if (isAdding) {
        // Adding new - empty means cancel
        if (!trimmed) {
          cancelEditing();
          return;
        }
        await onSave([...values, trimmed]);
      } else if (editingIndex !== null) {
        // Editing existing - empty means delete
        if (!trimmed) {
          await onSave(values.filter((_, i) => i !== editingIndex));
        } else if (trimmed !== values[editingIndex]) {
          await onSave(values.map((v, i) => (i === editingIndex ? trimmed : v)));
        }
      }
      cancelEditing();
    } catch {
      // Stay in edit mode - parent controls error display via prop
    }
  };

  const removeItem = async (index: number) => {
    try {
      await onSave(values.filter((_, i) => i !== index));
    } catch {
      // Parent controls error display
    }
  };

  return (
    <>
      {values.map((item, index) =>
        editingIndex === index ? (
          <InlineInputControl
            key={index}
            value={editValue}
            onChange={handleChange}
            onSave={save}
            onCancel={cancelEditing}
            placeholder={placeholder ? `${placeholder} (clear to delete)` : undefined}
            disabled={saving}
            error={error}
            icon={editIcon}
          />
        ) : (
          <div key={index} className="flex items-center gap-2">
            <div className="flex-1 min-w-0">
              <FieldDisplay
                icon={icon}
                href={item}
                target="_blank"
                canEdit={canEdit}
                onEdit={() => startEditing(index, item)}
              >
                {item}
              </FieldDisplay>
            </div>
            {canEdit && (
              <Button variant="ghost" size="icon-sm" onClick={() => removeItem(index)} disabled={saving}>
                <Icon i="close" className="size-4 text-muted-foreground" />
              </Button>
            )}
          </div>
        ),
      )}

      {isAdding ? (
        <InlineInputControl
          value={editValue}
          onChange={handleChange}
          onSave={save}
          onCancel={cancelEditing}
          placeholder={placeholder}
          disabled={saving}
          error={error}
          icon={editIcon}
        />
      ) : (
        canEdit && !editingIndex && <AddFieldButton icon={editIcon} label={addLabel} onClick={startAdding} />
      )}
    </>
  );
}
